import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/ui/tabs";
import {
	ChevronUp,
	GitFork,
	MessageSquare,
	ArrowLeft,
	Star,
	Clock,
	History,
	Pencil,
	Send,
} from "lucide-react";
import type { Idea, User, Comment } from "@/lib/types";

interface IdeaDetailProps {
	idea: Idea;
	author: User | undefined;
	comments: Comment[];
	users: Map<string, User>;
	currentUserId: string;
	forkedFrom: Idea | null;
	forks: Idea[];
	onVote: (ideaId: string) => void;
	onBack: () => void;
	onFork: (ideaId: string) => void;
	onEdit: (ideaId: string) => void;
	onAddComment: (ideaId: string, content: string) => void;
	onSelectIdea: (ideaId: string) => void;
}

function timeAgo(dateStr: string): string {
	const seconds = Math.floor(
		(Date.now() - new Date(dateStr).getTime()) / 1000,
	);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function Section({
	label,
	content,
}: {
	label: string;
	content: string;
}) {
	return (
		<div>
			<h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
				{label}
			</h4>
			<p className="text-sm leading-relaxed text-foreground">{content}</p>
		</div>
	);
}

export function IdeaDetail({
	idea,
	author,
	comments,
	users,
	currentUserId,
	forkedFrom,
	forks,
	onVote,
	onBack,
	onFork,
	onEdit,
	onAddComment,
	onSelectIdea,
}: IdeaDetailProps) {
	const [commentText, setCommentText] = useState("");
	const hasVoted = idea.votedBy.includes(currentUserId);
	const isAuthor = idea.authorId === currentUserId;

	const handleComment = () => {
		if (!commentText.trim()) return;
		onAddComment(idea.id, commentText.trim());
		setCommentText("");
	};

	return (
		<div className="space-y-6">
			{/* Back button */}
			<button
				onClick={onBack}
				className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
			>
				<ArrowLeft className="w-4 h-4" />
				Back to feed
			</button>

			{/* Main card */}
			<div className="rounded-xl border border-border bg-card p-6 space-y-5">
				{/* Header */}
				<div className="flex items-start gap-4">
					<button
						onClick={() => onVote(idea.id)}
						className={`flex flex-col items-center gap-0.5 px-4 py-3 rounded-lg border-2 transition-all duration-200 shrink-0 ${
							hasVoted
								? "border-orange-500 bg-orange-50 text-orange-600"
								: "border-border hover:border-orange-300 text-muted-foreground hover:text-orange-500"
						}`}
					>
						<ChevronUp
							className={`w-6 h-6 ${hasVoted ? "text-orange-500" : ""}`}
						/>
						<span className="text-lg font-bold">{idea.votes}</span>
						<span className="text-[10px] uppercase tracking-wide">
							votes
						</span>
					</button>

					<div className="flex-1 min-w-0">
						<h1 className="text-xl font-bold leading-tight mb-3">
							{idea.title}
						</h1>

						<div className="flex flex-wrap gap-1.5 mb-3">
							{idea.tags.map((tag) => (
								<Badge
									key={tag}
									variant="secondary"
									className="text-xs"
								>
									{tag}
								</Badge>
							))}
						</div>

						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							<div className="flex items-center gap-1.5">
								<Avatar className="w-6 h-6">
									<AvatarFallback className="text-[10px] bg-muted">
										{author?.avatar ?? "?"}
									</AvatarFallback>
								</Avatar>
								<span className="font-medium text-foreground">
									{author?.name ?? "Unknown"}
								</span>
								{author && (
									<span className="flex items-center text-amber-500">
										<Star className="w-3 h-3 mr-0.5" />
										{author.reputation}
									</span>
								)}
							</div>
							<span className="flex items-center gap-1">
								<Clock className="w-3.5 h-3.5" />
								{timeAgo(idea.createdAt)}
							</span>
							{idea.currentVersion > 1 && (
								<span className="text-emerald-600 font-medium">
									v{idea.currentVersion}
								</span>
							)}
						</div>

						{forkedFrom && (
							<div className="mt-2 flex items-center gap-1.5 text-xs text-blue-500">
								<GitFork className="w-3.5 h-3.5" />
								<span>Forked from</span>
								<button
									onClick={() => onSelectIdea(forkedFrom.id)}
									className="font-medium hover:underline"
								>
									{forkedFrom.title}
								</button>
							</div>
						)}
					</div>
				</div>

				<Separator />

				{/* Idea body */}
				<div className="grid gap-5">
					<Section label="Problem" content={idea.problem} />
					<Section label="Solution" content={idea.solution} />
					<Section label="Target Market" content={idea.targetMarket} />
					<Section label="Monetization" content={idea.monetization} />
				</div>

				<Separator />

				{/* Actions */}
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => onFork(idea.id)}
						className="gap-1.5"
					>
						<GitFork className="w-4 h-4" />
						Fork & Improve
					</Button>
					{isAuthor && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => onEdit(idea.id)}
							className="gap-1.5"
						>
							<Pencil className="w-4 h-4" />
							Edit
						</Button>
					)}
				</div>
			</div>

			{/* Tabs: Comments, Version History, Forks */}
			<Tabs defaultValue="comments" className="space-y-4">
				<TabsList className="bg-muted/50">
					<TabsTrigger value="comments" className="gap-1.5 text-xs">
						<MessageSquare className="w-3.5 h-3.5" />
						Comments ({comments.length})
					</TabsTrigger>
					<TabsTrigger value="history" className="gap-1.5 text-xs">
						<History className="w-3.5 h-3.5" />
						History ({idea.versions.length})
					</TabsTrigger>
					<TabsTrigger value="forks" className="gap-1.5 text-xs">
						<GitFork className="w-3.5 h-3.5" />
						Forks ({forks.length})
					</TabsTrigger>
				</TabsList>

				<TabsContent value="comments" className="space-y-4">
					{/* Comment form */}
					<div className="flex gap-3">
						<Textarea
							placeholder="Share your thoughts on this idea..."
							value={commentText}
							onChange={(e) => setCommentText(e.target.value)}
							className="min-h-[80px] resize-none"
						/>
						<Button
							size="sm"
							onClick={handleComment}
							disabled={!commentText.trim()}
							className="self-end shrink-0 bg-gradient-to-r from-orange-500 to-red-600 text-white"
						>
							<Send className="w-4 h-4" />
						</Button>
					</div>

					{/* Comment list */}
					<div className="space-y-3">
						{comments.map((comment) => {
							const commenter = users.get(comment.authorId);
							return (
								<div
									key={comment.id}
									className="p-4 rounded-lg border border-border bg-card"
								>
									<div className="flex items-center gap-2 mb-2">
										<Avatar className="w-6 h-6">
											<AvatarFallback className="text-[10px] bg-muted">
												{commenter?.avatar ?? "?"}
											</AvatarFallback>
										</Avatar>
										<span className="text-sm font-medium">
											{commenter?.name ?? "Unknown"}
										</span>
										<span className="text-xs text-muted-foreground">
											{timeAgo(comment.createdAt)}
										</span>
									</div>
									<p className="text-sm leading-relaxed text-foreground/90">
										{comment.content}
									</p>
								</div>
							);
						})}
						{comments.length === 0 && (
							<p className="text-center text-sm text-muted-foreground py-8">
								No comments yet. Be the first to share your thoughts!
							</p>
						)}
					</div>
				</TabsContent>

				<TabsContent value="history" className="space-y-3">
					{[...idea.versions].reverse().map((version) => {
						const vAuthor = users.get(version.authorId);
						return (
							<div
								key={version.id}
								className="p-4 rounded-lg border border-border bg-card space-y-3"
							>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Badge
											variant={
												version.version === idea.currentVersion
													? "default"
													: "outline"
											}
											className="text-xs"
										>
											v{version.version}
										</Badge>
										<span className="text-sm font-medium">
											{version.changeNote}
										</span>
									</div>
									<span className="text-xs text-muted-foreground">
										{formatDate(version.createdAt)}
									</span>
								</div>

								<div className="flex items-center gap-2 text-xs text-muted-foreground">
									<Avatar className="w-5 h-5">
										<AvatarFallback className="text-[9px] bg-muted">
											{vAuthor?.avatar ?? "?"}
										</AvatarFallback>
									</Avatar>
									<span>{vAuthor?.name ?? "Unknown"}</span>
								</div>

								<div className="grid gap-2 text-xs">
									<div>
										<span className="font-medium text-muted-foreground">
											Problem:{" "}
										</span>
										<span className="text-foreground/80">
											{version.problem.length > 150
												? version.problem.slice(0, 150) + "..."
												: version.problem}
										</span>
									</div>
									<div>
										<span className="font-medium text-muted-foreground">
											Solution:{" "}
										</span>
										<span className="text-foreground/80">
											{version.solution.length > 150
												? version.solution.slice(0, 150) + "..."
												: version.solution}
										</span>
									</div>
								</div>
							</div>
						);
					})}
				</TabsContent>

				<TabsContent value="forks" className="space-y-3">
					{forks.map((fork) => {
						const forkAuthor = users.get(fork.authorId);
						return (
							<button
								key={fork.id}
								onClick={() => onSelectIdea(fork.id)}
								className="w-full text-left p-4 rounded-lg border border-border bg-card hover:border-blue-200 hover:shadow-sm transition-all"
							>
								<div className="flex items-center justify-between mb-1">
									<h4 className="text-sm font-semibold">
										{fork.title}
									</h4>
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										<ChevronUp className="w-3.5 h-3.5" />
										{fork.votes}
									</div>
								</div>
								<p className="text-xs text-muted-foreground line-clamp-2 mb-2">
									{fork.solution}
								</p>
								<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
									<Avatar className="w-4 h-4">
										<AvatarFallback className="text-[8px] bg-muted">
											{forkAuthor?.avatar ?? "?"}
										</AvatarFallback>
									</Avatar>
									<span>{forkAuthor?.name ?? "Unknown"}</span>
									<span>{timeAgo(fork.createdAt)}</span>
								</div>
							</button>
						);
					})}
					{forks.length === 0 && (
						<p className="text-center text-sm text-muted-foreground py-8">
							No forks yet. Be the first to fork and improve this idea!
						</p>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
